import React, { useState } from 'react';
import { Table, Button, Tag, Typography, Alert, Empty } from 'antd';
import PageShell from '../components/page/PageShell';
import PageHeader from '../components/page/PageHeader';
import PageState from '../components/page/PageState';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useApp } from '../context/AppContext';
import { useSupportApi } from '../hooks/useSupportApi';
import { useResource } from '../hooks/useResource';
import TicketDetailDrawer from '../components/support/TicketDetailDrawer';
import RaiseTicketModal from '../components/support/RaiseTicketModal';
import { PRIORITY_COLOR, STATUS_COLOR, humanizeEnum, formatDdMmYyyy, formatDdMmYyyyTime, shortId } from '../utils/supportApi';

const { Title, Text } = Typography;

const PAGE_SIZE = 20;

/**
 * Customer-side support: a Lead Advisor or Company SPOC raises and follows their OWN tickets
 * with Prolense support. Cites: .claude/design/backoffice-ticketing-pathkey-sqa.md, Sub-workstream A.
 *
 * Scoping is entirely server-side (supportTicket.service.ts `listSupportTickets`): a non-staff
 * caller only ever receives rows matching their own tenantId AND reporterUserId, severity-sorted
 * and paginated, and internal notes are never returned to them. SupportPage mounts this only when
 * the server's /me reports the caller is NOT support staff. This page sends no tenantId and has no
 * status/assign controls because PATCH is route-gated to Prolense support staff.
 */
const MySupportPage = () => {
    const api = useSupportApi();
    const { activeProject, projects } = useApp();
    const [raiseOpen, setRaiseOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [page, setPage] = useState(1);

    const tickets = useResource(() => api.listTickets({ page, pageSize: PAGE_SIZE }), [api, page]);
    const rows = tickets.status === 'ready' ? tickets.data.tickets : [];
    const selected = selectedId ? rows.find((t) => t.id === selectedId) || null : null;

    // A reporter's own project label, from the portfolio already loaded for the shell.
    const labelFor = (projectId) => {
        const p = (projects || []).find((x) => x.id === projectId);
        return p ? p.companyName || p.name : undefined;
    };

    const columns = [
        {
            title: 'Ticket',
            dataIndex: 'title',
            render: (v, t) => (
                <div>
                    <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => setSelectedId(t.id)}>
                        {v}
                    </Button>
                    <div><Text type="secondary">{shortId(t.id)}</Text></div>
                </div>
            ),
        },
        { title: 'Category', dataIndex: 'category', render: (v) => humanizeEnum(v) },
        { title: 'Priority', dataIndex: 'priority', render: (v) => <Tag color={PRIORITY_COLOR[v]}>{humanizeEnum(v)}</Tag> },
        { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={STATUS_COLOR[v]}>{humanizeEnum(v)}</Tag> },
        { title: 'Target resolution', dataIndex: 'slaResolutionDueAt', render: (v) => formatDdMmYyyy(v) },
        { title: 'Raised', dataIndex: 'createdAt', render: (v) => formatDdMmYyyyTime(v) },
    ];

    const renderBody = () => {
        if (tickets.status === 'loading' || tickets.status === 'idle') return <PageState status="loading" />;
        if (tickets.status === 'error') {
            return (
                <Alert
                    type="error"
                    showIcon
                    message="Could not load your tickets"
                    description={tickets.error.message}
                    action={<Button onClick={tickets.reload}>Retry</Button>}
                />
            );
        }
        if (tickets.data.total === 0) {
            return (
                <Empty description="You have not raised any tickets.">
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setRaiseOpen(true)}>
                        Raise a ticket
                    </Button>
                </Empty>
            );
        }
        return (
            <Table
                rowKey={(r) => r.id}
                dataSource={rows}
                columns={columns}
                pagination={{
                    current: tickets.data.page,
                    pageSize: tickets.data.pageSize,
                    total: tickets.data.total,
                    showSizeChanger: false,
                    onChange: (p) => setPage(p),
                }}
            />
        );
    };

    return (
        <PageShell>
            <PageHeader
                title="Support"
                subtitle="Raise an issue with Prolense support and follow your own tickets. You only see tickets you raised."
                actions={(
                    <>
                        <Button icon={<ReloadOutlined />} onClick={tickets.reload}>Refresh</Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => setRaiseOpen(true)}>
                            Raise a ticket
                        </Button>
                    </>
                )}
            />
            {renderBody()}
            <RaiseTicketModal
                open={raiseOpen}
                api={api}
                activeProject={activeProject}
                onClose={() => setRaiseOpen(false)}
                onCreated={(t) => {
                    setRaiseOpen(false);
                    setPage(1);
                    tickets.reload();
                    setSelectedId(t.id);
                }}
            />
            <TicketDetailDrawer
                key={selected ? selected.id : 'none'}
                open={!!selected}
                ticket={selected}
                staff={false}
                api={api}
                projectLabel={selected && selected.projectId ? labelFor(selected.projectId) : undefined}
                onClose={() => setSelectedId(null)}
                onTicketChanged={tickets.reload}
                onActivity={tickets.reload}
            />
        </PageShell>
    );
};

export default MySupportPage;
